import React from 'react';
import { injectIntl } from 'react-intl';
import AdminShell, { Icon } from './AdminShell';
import DataTable from './DataTable';
import { Modal, Tabs, Summary } from './Overlay';
import { Fieldset, SettingRow, Switch, Field } from './Form';
import { text } from './peopleCopy';
import { api, contentPageUploadDir } from '../../config';

/*
 * Content pages — layar `content` paket client.
 *
 * Berbeda dari Static pages, semua kolom prototipe punya penyimpanan di sini:
 * pageUrl adalah alamatnya, isEnable status terbitnya, dan ada mutation untuk
 * membuat, menyunting, menerbitkan dan menghapus. Jadi layar ini mengikuti
 * prototipe apa adanya.
 *
 * Dua hal yang tetap harus dijaga:
 *
 *   - Menyimpan MENGHAPUS seluruh baris terjemahan halaman lalu menulis ulang
 *     yang dikirim, sama seperti halaman statis. Formulir ini mengambil ulang
 *     halaman lengkap dulu dan selalu mengirim semua bahasanya kembali.
 *
 *   - Alamatnya dilayani route tangkap-semua `/:pageUrl`, yang berada SETELAH
 *     seluruh route aplikasi. Alamat yang sama dengan route yang sudah ada
 *     tidak akan pernah tampil. Mutation menolaknya saat dibuat, tapi route
 *     baru bisa ditambahkan belakangan dan menutupi halaman yang sudah ada —
 *     jadi layarnya menandai baris yang tertutup.
 */

const gql = (query, variables) => fetch('/graphql', {
  method: 'post',
  headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
  body: JSON.stringify({ query, variables }),
  credentials: 'include'
}).then(r => r.json());

const Q = `query ($currentPage: Int, $limit: Int, $searchList: String, $state: String) {
  getAdminContentPages(currentPage: $currentPage, limit: $limit, searchList: $searchList, state: $state) {
    count total liveCount shadowedCount status errorMessage
    results {
      id pageTitle pageUrl metaTitle metaDescription pageBanner isEnable
      contentLength updatedAt shadowedByRoute
      languages { language length }
    }
  }
}`;

const Q_ONE = `query ($id: Int!) {
  getContentPageById(id: $id) {
    status
    result {
      id pageTitle pageUrl metaTitle metaDescription content pageBanner isEnable
      dynamicContentDetails { id contentId language content }
    }
  }
}`;

const M_SAVE = `mutation ($id: Int, $pageTitle: String, $pageUrl: String,
                 $metaTitle: String, $metaDescription: String, $content: String,
                 $pageBanner: String, $dynamicContentData: [dynamicContentInput]) {
  addContentPageDetails(id: $id, pageTitle: $pageTitle, pageUrl: $pageUrl,
                        metaTitle: $metaTitle, metaDescription: $metaDescription,
                        content: $content, pageBanner: $pageBanner,
                        dynamicContentData: $dynamicContentData) { status errorMessage }
}`;

const M_STATUS = `mutation ($id: Int, $isEnable: Boolean) {
  updateContentPageStatus(id: $id, isEnable: $isEnable) { status errorMessage }
}`;

const M_DELETE = `mutation ($id: Int!) {
  deleteContentPage(id: $id) { status errorMessage }
}`;

const ok = res => res && Number(res.status) === 200;

const LANG_LABEL = { 'en-US': 'EN', en: 'EN', ar: 'AR' };

const day = v => {
  const d = new Date(Number(v) || v);
  return isNaN(d) ? null : d.toISOString().slice(0, 10);
};

// Alamat dibuat dari judulnya selama belum disentuh — sama seperti prototipe.
const slugify = s => String(s || '').toLowerCase().trim()
  .replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').slice(0, 48);

class ContentPages extends React.Component {
  state = {
    rows: [], count: 0, total: 0, liveCount: 0, shadowedCount: 0,
    page: 1, perPage: 25, search: '', filterState: '',
    loading: true, error: null,
    form: null, loadingForm: false, tab: 0, formError: null, slugTouched: false,
    deleting: null, saving: false, notice: null
  };

  componentDidMount() { this.load(); }

  locale = () => this.props.intl && this.props.intl.locale;
  tx = (en, ar) => text(this.locale(), en, ar);

  vars = extra => ({
    currentPage: this.state.page,
    limit: this.state.perPage,
    searchList: this.state.search || undefined,
    state: this.state.filterState || undefined,
    ...extra
  });

  load = async quiet => {
    if (!quiet) this.setState({ loading: true, error: null });
    const json = await gql(Q, this.vars()).catch(() => null);
    const res = json && json.data && json.data.getAdminContentPages;
    if (!ok(res)) {
      this.setState({ loading: false, error: (res && res.errorMessage) || this.tx('Could not load content pages.', 'تعذر تحميل صفحات المحتوى.') });
      return;
    }
    this.setState({
      rows: res.results || [], count: res.count || 0, total: res.total || 0,
      liveCount: res.liveCount || 0, shadowedCount: res.shadowedCount || 0, loading: false
    });
  };

  setPage = page => this.setState({ page }, () => this.load());
  setPerPage = perPage => this.setState({ perPage, page: 1 }, () => this.load());
  setSearch = search => this.setState({ search, page: 1 }, () => this.load());
  pick = key => e => this.setState({ [key]: e.target.value, page: 1 }, () => this.load());

  openAdd = () => this.setState({
    form: {
      id: null, pageTitle: '', pageUrl: '', metaTitle: '', metaDescription: '',
      content: '', pageBanner: '', langs: {}, langOrder: ['en-US']
    },
    langsInit: true, slugTouched: false, formError: null, notice: null, tab: 0
  });

  /*
   * Selalu ambil ulang halaman lengkap. Daftarnya tidak memuat naskah, dan
   * menyimpan tanpa naskah di tangan akan mengosongkan halamannya — sementara
   * mengirim tanpa daftar bahasa lengkap akan menghapus terjemahannya.
   */
  openEdit = async row => {
    this.setState({ loadingForm: true, formError: null, notice: null, tab: 0 });
    const json = await gql(Q_ONE, { id: row.id }).catch(() => null);
    const res = json && json.data && json.data.getContentPageById;
    const page = res && res.result;

    if (!ok(res) || !page) {
      this.setState({
        loadingForm: false,
        notice: { tone: 'danger', text: this.tx('Could not open that page. Nothing was changed.', 'تعذر فتح الصفحة. لم يتم تغيير أي شيء.') }
      });
      return;
    }

    const langs = {};
    (page.dynamicContentDetails || []).forEach(d => {
      langs[d.language] = { content: d.content || '' };
    });
    const langOrder = Object.keys(langs);

    this.setState({
      loadingForm: false, slugTouched: true,
      form: {
        id: page.id,
        pageTitle: page.pageTitle || '',
        pageUrl: page.pageUrl || '',
        metaTitle: page.metaTitle || '',
        metaDescription: page.metaDescription || '',
        content: page.content || '',
        pageBanner: page.pageBanner || '',
        langs,
        langOrder: langOrder.length ? langOrder : ['en-US']
      }
    });
  };

  setForm = (key, value) => this.setState(st => {
    const form = { ...st.form, [key]: value };
    // Alamat mengikuti judul sampai seseorang menyentuhnya sendiri.
    if (key === 'pageTitle' && !st.slugTouched) form.pageUrl = slugify(value);
    return { form };
  });

  setLang = (language, content) => this.setState(st => ({
    form: {
      ...st.form,
      langs: { ...st.form.langs, [language]: { content } },
      langOrder: st.form.langOrder.indexOf(language) >= 0
        ? st.form.langOrder : st.form.langOrder.concat(language)
    }
  }));

  addArabic = () => this.setState(st => ({
    form: {
      ...st.form,
      langs: { ...st.form.langs, ar: { content: '' } },
      langOrder: st.form.langOrder.indexOf('ar') >= 0
        ? st.form.langOrder : st.form.langOrder.concat('ar')
    },
    tab: st.form.langOrder.length + 1
  }));

  submit = async () => {
    const f = this.state.form;
    if (!f.pageTitle.trim() || !f.pageUrl.trim() || !f.metaTitle.trim()) {
      this.setState({ formError: this.tx('Title, address and browser title are all required.', 'العنوان والرابط وعنوان المتصفح مطلوبة.') });
      return;
    }
    if (!/^[a-z0-9-]+$/.test(f.pageUrl.trim())) {
      this.setState({ formError: this.tx('The address may only contain lowercase letters, numbers and hyphens.', 'لا يمكن أن يحتوي الرابط إلا على أحرف صغيرة وأرقام وواصلات.') });
      return;
    }
    this.setState({ saving: true, formError: null });

    // Semua bahasa dikirim, bukan hanya yang disunting: mutation menghapus
    // seluruh baris terjemahan halaman lalu membuat ulang dari daftar ini.
    const dynamicContentData = f.langOrder.map(language => ({
      language,
      content: (f.langs[language] && f.langs[language].content) || ''
    }));

    const json = await gql(M_SAVE, {
      id: f.id || undefined,
      pageTitle: f.pageTitle.trim(),
      pageUrl: f.pageUrl.trim(),
      metaTitle: f.metaTitle.trim(),
      metaDescription: f.metaDescription,
      content: f.content,
      pageBanner: f.pageBanner || undefined,
      dynamicContentData
    }).catch(() => null);
    const res = json && json.data && json.data.addContentPageDetails;
    this.setState({ saving: false });

    if (!ok(res)) {
      this.setState({ formError: (res && res.errorMessage) || this.tx('Could not save the page.', 'تعذر حفظ الصفحة.') });
      return;
    }
    this.setState({
      form: null,
      notice: {
        tone: 'info',
        text: f.id
          ? `${f.pageTitle.trim()} saved.`
          : `${f.pageTitle.trim()} is live at /${f.pageUrl.trim()}.`
      }
    });
    this.load(true);
  };

  toggleStatus = async r => {
    this.setState({ saving: true, notice: null });
    /*
     * DIKIRIM NILAI SEKARANG, BUKAN NILAI YANG DIINGINKAN. updateContentPageStatus
     * menulis `!isEnable`: argumennya diperlakukan sebagai keadaan lama dan
     * dibalik. Mengirim nilai yang diinginkan membuat mutation membalas 200
     * sambil tidak mengubah apa pun — halaman tetap tayang setelah ditekan
     * "jadikan draf". Sama persis dengan updateCurrency di layar Currencies.
     */
    const json = await gql(M_STATUS, { id: r.id, isEnable: r.isEnable }).catch(() => null);
    const res = json && json.data && json.data.updateContentPageStatus;
    this.setState({ saving: false });
    this.setState({
      notice: !ok(res)
        ? { tone: 'danger', text: (res && res.errorMessage) || this.tx('Could not change the status.', 'تعذر تغيير الحالة.') }
        : {
          tone: 'info',
          text: r.isEnable
            ? `${r.pageTitle} ${this.tx('is now a draft.', 'أصبحت مسودة.')} /${r.pageUrl}`
            : `${r.pageTitle} ${this.tx('is live at', 'منشورة على')} /${r.pageUrl}.`
        }
    });
    this.load(true);
  };

  remove = async () => {
    const r = this.state.deleting;
    this.setState({ saving: true });
    const json = await gql(M_DELETE, { id: r.id }).catch(() => null);
    const res = json && json.data && json.data.deleteContentPage;
    this.setState({ saving: false, deleting: null });
    this.setState({
      notice: !ok(res)
        ? { tone: 'danger', text: (res && res.errorMessage) || this.tx('Could not delete the page.', 'تعذر حذف الصفحة.') }
        : { tone: 'danger', text: `${r.pageTitle} ${this.tx('was deleted.', 'تم حذفها.')} /${r.pageUrl} ${this.tx('now returns a 404.', 'يعيد الآن خطأ 404.')}` }
    });
    this.load(true);
  };

  columns() {
    const t = this.tx;
    return [
      { key: 'pageTitle', label: t('Page', 'الصفحة'), sortable: true, render: r => <b>{r.pageTitle}</b> },
      {
        key: 'pageBanner', label: t('Image', 'الصورة'),
        render: r => r.pageBanner
          ? <img src={`${api.apiEndpoint}${contentPageUploadDir}large_${r.pageBanner}`} alt="" style={{ width: 42, height: 28, objectFit: 'cover', borderRadius: 5 }} />
          : <span className="tag">{t('None', 'لا يوجد')}</span>
      },
      {
        key: 'pageUrl', label: t('URL', 'الرابط'), sortable: true,
        render: r => (
          <span>
            <span className="mono">/{r.pageUrl}</span>
            {/* Route aplikasi terdaftar lebih dulu daripada tangkap-semua
                /:pageUrl, jadi halaman dengan alamat yang sama tidak pernah
                tampil — meski statusnya Live. */}
            {r.shadowedByRoute && (
              <span className="tag danger" style={{ marginInlineStart: 8 }}>
                <i />{t('Hidden by an app route', 'مخفي بسبب مسار التطبيق')}
              </span>
            )}
          </span>
        )
      },
      {
        key: 'languages', label: t('Languages', 'اللغات'),
        render: r => {
          const has = (r.languages || []).filter(l => l.length > 0).map(l => l.language);
          if (!has.length) return <span className="tag danger"><i />{t('None', 'لا يوجد')}</span>;
          return (
            <span style={{ display: 'inline-flex', gap: 5, flexWrap: 'wrap' }}>
              {has.map(l => (
                <span className={`tag ${l === 'ar' ? 'amber' : ''}`} key={l}>
                  <i />{LANG_LABEL[l] || l}
                </span>
              ))}
            </span>
          );
        }
      },
      {
        key: 'updatedAt', label: t('Updated', 'آخر تحديث'), align: 'end', sortable: true,
        render: r => <span className="mono">{day(r.updatedAt) || '—'}</span>
      },
      {
        key: 'isEnable', label: t('Status', 'الحالة'),
        render: r => (
          <span className={`tag ${r.isEnable ? 'ok' : ''}`}>
            <i />{r.isEnable ? t('Live', 'منشور') : t('Draft', 'مسودة')}
          </span>
        )
      },
      {
        key: 'act', label: '', align: 'end',
        render: r => (
          <span style={{ display: 'inline-flex', gap: 6 }}>
            <a className="btn icon" title={t('Preview page', 'معاينة الصفحة')} aria-label={`${t('Preview', 'معاينة')} ${r.pageTitle}`}
              href={`/${r.pageUrl}`} target="_blank" rel="noopener noreferrer">
              <Icon name="eye" />
            </a>
            <button className="btn icon" type="button" title={t('Edit page', 'تعديل الصفحة')}
              aria-label={`${t('Edit', 'تعديل')} ${r.pageTitle}`}
              disabled={this.state.loadingForm}
              onClick={() => this.openEdit(r)}>
              <Icon name="edit" />
            </button>
            <button className="btn icon" type="button"
              title={r.isEnable ? t('Turn into a draft', 'تحويل إلى مسودة') : t('Publish', 'نشر')}
              aria-label={`${t('Toggle', 'تبديل')} ${r.pageTitle}`}
              onClick={() => this.toggleStatus(r)}>
              <Icon name={r.isEnable ? 'x' : 'check'} />
            </button>
            <button className="btn icon" type="button" title={t('Delete page', 'حذف الصفحة')}
              aria-label={`${t('Delete', 'حذف')} ${r.pageTitle}`}
              onClick={() => this.setState({ deleting: r, notice: null })}>
              <Icon name="trash" />
            </button>
          </span>
        )
      }
    ];
  }

  renderForm() {
    const f = this.state.form;
    const { saving, formError, tab } = this.state;
    const langs = f.langOrder;
    const t = this.tx;
    const labels = [t('Page', 'الصفحة')].concat(langs.map(l => `${LANG_LABEL[l] || l} ${t('content', 'المحتوى')}`));
    const hasArabic = langs.indexOf('ar') >= 0;

    return (
      <Modal
        icon="doc" wide
        title={f.id ? `${t('Edit', 'تعديل')} ${f.pageTitle}` : t('Add content page', 'إضافة صفحة محتوى')}
        sub={f.id
          ? `${t('Served at', 'تُعرض على')} /${f.pageUrl}`
          : t('A new page goes live at its address immediately.', 'تُنشر الصفحة الجديدة فوراً على رابطها.')}
        confirmLabel={f.id ? t('Save page', 'حفظ الصفحة') : t('Create page', 'إنشاء الصفحة')}
        busy={saving}
        disabled={!f.pageTitle.trim() || !f.pageUrl.trim() || !f.metaTitle.trim()}
        onConfirm={this.submit}
        onClose={() => this.setState({ form: null, formError: null })}
      >
        {formError && (
          <div className="notice danger">
            <span className="ni">!</span><span className="nt">{formError}</span>
          </div>
        )}

        <div className="notice">
          <span className="ni">i</span>
          <span className="nt">
            {t(`Saving rewrites every language of this page at once. All ${langs.length} loaded here are sent back together, so editing one never drops another.`, `يحفظ هذا جميع لغات الصفحة دفعة واحدة. يتم إرسال اللغات المحملة وعددها ${langs.length} معاً حتى لا تُفقد لغة عند تعديل أخرى.`)}
          </span>
        </div>

        {/* addContentPageDetails tidak menerima isEnable, dan kolomnya berdefault
            1, jadi halaman baru langsung tayang. Disebutkan di sini karena
            tidak ada cara membuatnya sebagai draf lebih dulu. */}
        {!f.id && (
          <div className="notice danger">
            <span className="ni">!</span>
            <span className="nt">
              {t(`A new page goes live as soon as it is created — there is no way to save it as a draft first. Anyone reaching /${f.pageUrl || 'address'} will see it. Turn it into a draft from the list afterwards if it is not ready.`, `تُنشر الصفحة الجديدة فور إنشائها ولا يمكن حفظها كمسودة أولاً. سيرى أي شخص يزور /${f.pageUrl || 'address'} الصفحة. حوّلها إلى مسودة من القائمة إذا لم تكن جاهزة.`)}
            </span>
          </div>
        )}

        <Tabs labels={labels} active={tab} onChange={i => this.setState({ tab: i })} />

        <div style={{ marginTop: 'var(--sp-4)' }}>
          {tab === 0 && (
            <div>
              <Fieldset title={t('Page', 'الصفحة')}>
                <Field label={t('Page title', 'عنوان الصفحة')} required>
                  <input className="inp" value={f.pageTitle} placeholder={t('How towing works', 'كيفية سحب السيارة')}
                    onChange={e => this.setForm('pageTitle', e.target.value)} />
                </Field>
                <Field label={t('Address', 'الرابط')} required
                  hint={t('Lowercase letters, numbers and hyphens. This becomes the URL.', 'أحرف صغيرة وأرقام وواصلات فقط. يصبح هذا هو الرابط.') }>
                  <input className="inp mono" value={f.pageUrl} placeholder={t('how-towing-works', 'كيفية-سحب-السيارة')}
                    onChange={e => this.setState({ slugTouched: true },
                      () => this.setForm('pageUrl', e.target.value))} />
                </Field>
                <Field label={t('Main content (HTML)', 'المحتوى الرئيسي (HTML)')} span
                  hint={t('Served as-is on the page.', 'يُعرض كما هو في الصفحة.') }>
                  <textarea className="inp" rows={8} value={f.content}
                    onChange={e => this.setForm('content', e.target.value)} />
                </Field>
              </Fieldset>

              <Fieldset title={t('Search engine and tab', 'محرك البحث وعلامة التبويب')}>
                <Field label={t('Browser title', 'عنوان المتصفح')} required>
                  <input className="inp" value={f.metaTitle}
                    onChange={e => this.setForm('metaTitle', e.target.value)} />
                </Field>
                <Field label={t('Meta description', 'وصف التعريف')}>
                  <input className="inp" value={f.metaDescription}
                    onChange={e => this.setForm('metaDescription', e.target.value)} />
                </Field>
              </Fieldset>

              <Fieldset title={t('Page image', 'صورة الصفحة')}>
                {f.pageBanner
                  ? <div><img src={`${api.apiEndpoint}${contentPageUploadDir}large_${f.pageBanner}`} alt={f.pageTitle} style={{ maxWidth: '100%', maxHeight: 180, objectFit: 'cover', borderRadius: 8 }} /><div className="hint mono">{f.pageBanner}</div></div>
                  : <span className="tag danger"><i />{t('No image', 'لا توجد صورة')}</span>}
              </Fieldset>

              {!hasArabic && (
                <Fieldset title={t('Languages', 'اللغات')} rows>
                  <SettingRow label={t('Arabic version', 'النسخة العربية')}
                    hint={t('The site is bilingual. Without an Arabic version, Arabic readers see the English text.', 'الموقع ثنائي اللغة. بدون نسخة عربية سيرى القراء العرب النص الإنجليزي.') }>
                    <button className="btn ghost sm" type="button" onClick={this.addArabic}>
                      <Icon name="plus" />{t('Add Arabic', 'إضافة العربية')}
                    </button>
                  </SettingRow>
                </Fieldset>
              )}
            </div>
          )}

          {langs.map((l, i) => tab === i + 1 && (
            <Fieldset key={l} title={`${LANG_LABEL[l] || l} ${t('content', 'المحتوى')}`}
              desc={t('HTML shown to readers using this language.', 'HTML المعروض للقراء بهذه اللغة.') }>
              <Field label={t('Content (HTML)', 'المحتوى (HTML)')} span>
                <textarea className="inp" rows={12} dir={l === 'ar' ? 'rtl' : 'ltr'}
                  value={(f.langs[l] && f.langs[l].content) || ''}
                  onChange={e => this.setLang(l, e.target.value)} />
              </Field>
            </Fieldset>
          ))}
        </div>
      </Modal>
    );
  }

  render() {
    const {
      rows, count, total, liveCount, shadowedCount, page, perPage,
      loading, error, filterState, form, loadingForm, deleting, saving, notice
    } = this.state;
    const t = this.tx;

    return (
      <AdminShell
        current="content"
        breadcrumb={[t('Content', 'المحتوى'), t('Content pages', 'صفحات المحتوى')]}
        title={t('Content pages', 'صفحات المحتوى')}
        lead={total
          ? (this.locale() === 'ar' ? `${total} مقالة، ${liveCount} منشورة على الموقع.` : `${total} article${total === 1 ? '' : 's'}, ${liveCount} live on the website.`)
          : t('Marketing and help articles. None created yet.', 'مقالات تسويقية وإرشادية. لم يتم إنشاء أي منها بعد.')}
        actions={[
          <a className="btn ghost" href="/siteadmin/contentpage/manage" key="c">{t('Open in classic', 'فتح النسخة الكلاسيكية')}</a>,
          <button className="btn" type="button" key="a" onClick={this.openAdd}>
            <Icon name="plus" />{t('Add page', 'إضافة صفحة')}
          </button>
        ]}
      >
        {notice && (
          <div className={`notice ${notice.tone}`}>
            <span className="ni">{notice.tone === 'danger' ? '!' : '✓'}</span>
            <span className="nt">{notice.text}</span>
            <button className="btn ghost sm" type="button"
              onClick={() => this.setState({ notice: null })}>{t('Dismiss', 'إغلاق')}</button>
          </div>
        )}

        {shadowedCount > 0 && (
          <div className="notice danger">
            <span className="ni">!</span>
            <span className="nt">
              {this.locale() === 'ar' ? `${shadowedCount} صفحة تستخدم روابط محجوزة للموقع نفسه. هذه الروابط تعرض الصفحة المدمجة وليس المقالة، بغض النظر عن الحالة هنا.` : `${shadowedCount} page${shadowedCount === 1 ? ' has an address' : 's have addresses'} already used by the website itself. Those addresses serve the built-in page, not the article, whatever the status here says.`}
            </span>
          </div>
        )}

        {loadingForm && (
          <div className="notice">
            <span className="ni">i</span>
            <span className="nt">{t('Loading every language of that page…', 'جارٍ تحميل كل لغات الصفحة…')}</span>
          </div>
        )}

        {form && this.renderForm()}

        {deleting && (
          <Modal
            icon="trash" tone="danger"
            title={`${t('Delete', 'حذف')} ${deleting.pageTitle}؟`}
            sub={`/${deleting.pageUrl}`}
            confirmLabel={t('Delete page', 'حذف الصفحة')}
            busy={saving}
            onConfirm={this.remove}
            onClose={() => this.setState({ deleting: null })}
          >
            <Summary pairs={[
              [t('Address', 'الرابط'), <span className="mono">/{deleting.pageUrl}</span>],
              [t('Status', 'الحالة'), deleting.isEnable ? t('Live', 'منشور') : t('Draft', 'مسودة')],
              [t('Languages', 'اللغات'), (deleting.languages || []).length || t('None', 'لا يوجد')]
            ]} />
            <div className={`notice ${deleting.isEnable ? 'danger' : ''}`}
              style={{ marginBottom: 0 }}>
              <span className="ni">{deleting.isEnable ? '!' : 'i'}</span>
              <span className="nt">
                {deleting.isEnable
                  ? t(`This page is live. Anyone following a link to /${deleting.pageUrl} will get a 404 afterwards. To take it down and keep the text, turn it into a draft instead.`, `هذه الصفحة منشورة. سيحصل من يزور /${deleting.pageUrl} على 404 بعد حذفها. لإيقافها مع الاحتفاظ بالنص، حوّلها إلى مسودة.`)
                  : t('This page is a draft, so nothing on the website links to it.', 'هذه الصفحة مسودة، لذلك لا يوجد رابط لها على الموقع.')}
              </span>
            </div>
          </Modal>
        )}

        {error
          ? <div className="notice danger"><span className="ni">!</span><span className="nt">{error}</span></div>
          : (
            <DataTable
              columns={this.columns()}
              rows={rows}
              count={count}
              page={page}
              perPage={perPage}
              loading={loading}
              onPage={this.setPage}
              onPerPage={this.setPerPage}
              onSearch={this.setSearch}
              searchPlaceholder={t('Search title or address', 'ابحث عن العنوان أو الرابط')}
              filters={
                <select className="inp" value={filterState} onChange={this.pick('filterState')}
                  aria-label={t('Filter by status', 'تصفية حسب الحالة')}>
                  <option value="">{t('Status: all', 'الحالة: الكل')}</option>
                  <option value="live">{t('Live', 'منشور')}</option>
                  <option value="draft">{t('Draft', 'مسودة')}</option>
                </select>
              }
              emptyText={t('No content pages yet', 'لا توجد صفحات محتوى بعد')}
              emptyHint={t('Add one to publish a help or marketing article at its own address.', 'أضف صفحة لنشر مقال إرشادي أو تسويقي على رابطه الخاص.')}
            />
          )}
      </AdminShell>
    );
  }
}

export default injectIntl(ContentPages);
